import { findModeByExtension, findModeByFileName } from 'codemirror';
import * as _ from 'lodash';
import type { Editor } from 'codemirror';
import type { VoidFunctionComponent } from 'react';

import { FileViewerFileContentsCodeMirror } from '../../../../shared/revisions/FileViewer/FileContents';

import Empty from './CodeMirrorEmpty';
import Loading from './CodeMirrorLoader';

import styles from './SnippetsDrawerCode.module.less';

//  Determines the "mode" for CodeMirror to display a given file.
const getCodeMirrorModeFromContentType = (fileName: string): ReturnType<typeof findModeByFileName> => {
  const fileExtension = _.last(fileName.split('.')) || 'txt';
  return findModeByExtension(fileExtension) || findModeByFileName(fileName);
};

type Props = {
  /**
   * If we are fetching the contents of a file right now.
   */
  isLoading: boolean;
  /**
   * The path the user has selected in the file tree.
   */
  selectedPath: string | null;
  /**
   * Contents of the file to display.
   */
  fileContents: string;
  /**
   * What happens when the editor mounts (we highlight the text)
   * @param editor CodeMirror editor that has just been mounted.
   */
  editorDidMount: (editor: Editor) => void;
};

/**
 * Renders the actual CodeMirror component, with fallbacks for empty and loading states.
 */
const CodeMirror: VoidFunctionComponent<Props> = ({
  isLoading,
  selectedPath,
  fileContents,
  editorDidMount,
}) => {
  //  If there is no selected path, we display a fallback "select a file" message
  const hasSelectedPath = !!selectedPath;

  if (!hasSelectedPath) {
    return (
      <Empty>
        Select a file from the tree on the left to view snippet matches.
      </Empty>
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  if (!fileContents) {
    return (
      <Empty>
        Code cannot be displayed at this time.
      </Empty>
    );
  }

  //  Determine the "mode" for Codemirror
  const fileName = selectedPath && _.last(selectedPath.split('/'));
  const mode = getCodeMirrorModeFromContentType(fileName || '');

  return (
    <FileViewerFileContentsCodeMirror
      className={styles.codeMirror}
      mode={mode}
      fileContents={fileContents}
      editorDidMount={editorDidMount}
    />
  );
};

export default CodeMirror;
